<script lang="ts">
	let {
		hoverable = false,
		interactive = false,
		variant = 'default' as 'default' | 'elevated' | 'bordered',
		class: className = '',
		children,
		...restProps
	} = $props();
	
	const classes = $derived([
		'card',
		`card--${variant}`,
		hoverable && 'card--hoverable',
		interactive && 'card--interactive',
		className
	].filter(Boolean).join(' '));
	
	const tag = $derived(interactive ? 'button' : 'div');
</script>

<svelte:element
	this={tag}
	class={classes}
	role={interactive ? 'button' : undefined}
	tabindex={interactive ? 0 : undefined}
	{...restProps}
>
	{@render children?.()}
</svelte:element>

<style>
	.card {
		position: relative;
		background: var(--bg-elevated);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		padding: var(--space-6);
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		overflow: hidden;
	}
	
	.card::before {
		content: '';
		position: absolute;
		inset: 0;
		background: var(--gradient-mesh);
		opacity: 0.03;
		pointer-events: none;
	}
	
	/* Variants */
	.card--default {
		box-shadow: var(--shadow-sm);
		backdrop-filter: blur(var(--backdrop-blur-sm));
	}
	
	.card--elevated {
		box-shadow: var(--shadow-md);
		border-color: var(--border-glass);
		background: var(--bg-glass);
		backdrop-filter: blur(var(--backdrop-blur-md));
	}
	
	.card--bordered {
		border-color: var(--border-default);
		box-shadow: none;
	}
	
	/* Hoverable state */
	.card--hoverable {
		cursor: pointer;
	}
	
	.card--hoverable:hover {
		transform: translateY(-2px) scale(1.002);
		box-shadow: var(--shadow-lg);
		border-color: var(--border-default);
	}
	
	.card--hoverable:hover::before {
		opacity: 0.05;
	}
	
	/* Interactive state */
	.card--interactive {
		cursor: pointer;
		user-select: none;
	}
	
	.card--interactive:hover {
		background: var(--bg-secondary);
		border-color: var(--border-default);
	}
	
	.card--interactive:active {
		transform: scale(0.98);
	}
	
	.card--interactive:focus-visible {
		outline: 2px solid var(--focus-ring);
		outline-offset: 2px;
	}
	
	/* Combination of hoverable and interactive */
	.card--hoverable.card--interactive:hover {
		transform: translateY(-4px);
		box-shadow: var(--shadow-xl);
	}
	
	.card--hoverable.card--interactive:active {
		transform: translateY(-2px) scale(0.98);
	}
</style>